import {ReactElement} from "react";
import {BORDER_RADIUS_LARGE, PD_LG, PD_MD, PD_XLG, PD_XLG2, PD_XLG3} from "@/shared/dimens";
import {StyledBoxColumn, StyledBoxRow, StyledHeadingVariant} from "@/shared/StyledComponents";
import {LargeButton} from "./SharedComponents";
import {useMediaQuery, useTheme} from "@mui/material";
import {HeroSectionData, SiteTemplate} from "../../types";

export function HeroSection(props: { data: HeroSectionData, template: SiteTemplate }): ReactElement {
  const theme = useTheme();
  const md = useMediaQuery(theme.breakpoints.up("md"));
  const sm = useMediaQuery(theme.breakpoints.up("sm"));

  // Split title on newline: all lines except last = white, last line = lime
  const lines = (props.data.title ?? "").split("\n").filter(l => l.trim());
  const hasMultipleLines = lines.length > 1;
  const firstLines = hasMultipleLines ? lines.slice(0, -1).join("\n") : lines[0] ?? "";
  const lastLine = hasMultipleLines ? lines[lines.length - 1] : null;

  return <div style={{
    width: "100%",
    display: "flex",
    flexWrap: md ? "nowrap" : "wrap",
    minHeight: md ? "72vh" : sm ? "56vh" : "auto",
    overflow: "hidden",
    background: theme.palette.background.default,
  }}>
    {/* Left text pane */}
    <div style={{
      flex: md ? "0 0 55%" : "0 0 100%",
      display: "flex",
      alignItems: "center",
      padding: md ? `${PD_XLG3}px` : sm ? `${PD_XLG2}px` : `${PD_XLG}px ${PD_LG}px`,
      minHeight: md ? "72vh" : sm ? "56vh" : "auto",
      boxSizing: "border-box",
    }}>
      <StyledBoxColumn style={{gap: PD_XLG, maxWidth: 560}}>
        <StyledBoxColumn style={{gap: PD_MD}}>
          <div style={{
            width: 52,
            height: 4,
            backgroundColor: theme.palette.secondary.main,
            borderRadius: 2,
          }}/>
          <StyledHeadingVariant default_={1} style={{color: "white", lineHeight: 1.05}}>
            {firstLines}
          </StyledHeadingVariant>
          {lastLine
            ? <StyledHeadingVariant default_={1} style={{color: theme.palette.secondary.main, lineHeight: 1.05}}>
              {lastLine}
            </StyledHeadingVariant>
            : null}
        </StyledBoxColumn>
        <StyledBoxRow style={{gap: PD_LG, flexWrap: "wrap"}}>
          {props.data.primaryLink?.text
            ? <LargeButton
              href={props.data.primaryLink.url}
              variant={"contained"}
              color={"secondary"}>
              {props.data.primaryLink.text}
            </LargeButton>
            : null}
          {props.data.secondaryLink?.text
            ? <LargeButton
              href={props.data.secondaryLink.url}
              variant={"outlined"}
              style={{color: "white", borderColor: "rgba(255,255,255,0.4)"}}>
              {props.data.secondaryLink.text}
            </LargeButton>
            : null}
        </StyledBoxRow>
      </StyledBoxColumn>
    </div>

    {/* Right image pane — desktop only */}
    {md && props.data.imageUrl
      ? <div style={{
        flex: "0 0 45%",
        position: "relative",
        overflow: "hidden",
      }}>
        <img
          src={props.data.imageUrl}
          style={{
            position: "absolute",
            width: "100%",
            height: "100%",
            objectFit: "cover",
            top: PD_XLG2,
            borderRadius: BORDER_RADIUS_LARGE,
            right: PD_XLG2,
            objectPosition: "right top",
          }}/>
        <div style={{
          position: "absolute",
          inset: 0,
          background: "linear-gradient(to right, rgba(13,41,24,0.65) 0%, rgba(13,41,24,0.1) 45%, transparent 100%)",
        }}/>
      </div>
      : null}

    {/* Mobile: show image below as a 16/9 strip */}
    {!md && props.data.imageUrl
      ? <div style={{
        flex: "0 0 100%",
        position: "relative",
        aspectRatio: "16/7",
        overflow: "hidden",
      }}>
        <img
          src={props.data.imageUrl}
          style={{
            position: "absolute",
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: "center top",
          }}/>
      </div>
      : null}
  </div>;
}
